import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getSeedSchedulerSettings,
  updateSeedSchedulerSettings,
} from "@/lib/seed-scheduler.functions";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, AlertTriangle, CheckCircle2, Play, Loader2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/seed-scheduler")({
  component: SeedSchedulerPage,
  head: () => ({ meta: [{ title: "Seed-Scheduler — InboxPulse" }] }),
});

function SeedSchedulerPage() {
  const qc = useQueryClient();
  const getFn = useServerFn(getSeedSchedulerSettings);
  const updateFn = useServerFn(updateSeedSchedulerSettings);

  const { data, isLoading } = useQuery({
    queryKey: ["seed-scheduler"],
    queryFn: () => getFn({ data: undefined }),
    refetchInterval: 10_000,
  });

  const [form, setForm] = useState({
    enabled: false,
    interval_minutes: 15,
    lookback_minutes: 30,
    scanner_url: "",
    scanner_secret: "",
  });

  useEffect(() => {
    if (data?.settings) {
      setForm({
        enabled: data.settings.enabled,
        interval_minutes: data.settings.interval_minutes,
        lookback_minutes: data.settings.lookback_minutes,
        scanner_url: data.settings.scanner_url ?? "",
        scanner_secret: data.settings.scanner_secret ?? "",
      });
    }
  }, [data?.settings]);

  const save = useMutation({
    mutationFn: () =>
      updateFn({
        data: {
          enabled: form.enabled,
          interval_minutes: form.interval_minutes,
          lookback_minutes: form.lookback_minutes,
          scanner_url: form.scanner_url.trim() || null,
          scanner_secret: form.scanner_secret.trim() || null,
        },
      }),
    onSuccess: () => {
      toast.success("Scheduler-Einstellungen gespeichert");
      qc.invalidateQueries({ queryKey: ["seed-scheduler"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const triggerManual = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/public/hooks/scan-seed-inboxes?force=1", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string,
        },
        body: "{}",
      });
      if (!res.ok) throw new Error(`Hook antwortete ${res.status}`);
      return res.json();
    },
    onSuccess: (r: {
      inboxes?: number;
      inserted?: number;
      errors?: number;
      skipped?: string;
      next_in_ms?: number;
    }) => {
      if (r.skipped === "disabled") {
        toast.message("Scheduler ist deaktiviert – kein Scan ausgeführt.");
      } else if (r.skipped === "throttled") {
        const mins = Math.ceil((r.next_in_ms ?? 0) / 60_000);
        toast.message(
          `Throttled – nächster Scan in ca. ${mins} Min. (mit "Jetzt scannen" wird das Intervall umgangen).`,
        );
      } else {
        toast.success(
          `Scan ausgeführt: ${r.inboxes ?? 0} Inboxes, ${r.inserted ?? 0} Ergebnisse, ${r.errors ?? 0} Fehler`,
        );
      }
      qc.invalidateQueries({ queryKey: ["seed-scheduler"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const s = data?.settings;
  const runs = data?.runs ?? [];

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Seed-Scheduler"
        description="Automatisches Testen aller Seed-Inboxes in konfigurierbaren Intervallen."
      />

      <HelpPanel
        title="Hilfe: Automatische Spam-Tests planen"
        what={
          <>
            Hier stellst du ein, wie oft das System <b>automatisch eine Test-Mail</b> an deine
            "Seed-Inboxes" (Test-Postfächer bei Gmail, Outlook, …) schickt. Damit erkennst du
            sofort, wenn deine Mails plötzlich im Spam landen.
          </>
        }
        why={
          <>
            Spam-Filter ändern sich täglich. Wer nicht regelmäßig testet, merkt erst nach 1000
            verschickten Mails, dass keine ankam. Mit einem Plan bist du am nächsten Tag gewarnt.
          </>
        }
        how={[
          <>
            <b>Intervall wählen</b>: z.B. alle 6 Stunden oder einmal pro Tag.
          </>,
          <>
            <b>Aktivieren</b> — fertig. Der Scheduler läuft automatisch im Hintergrund.
          </>,
          <>
            Ergebnisse siehst du unter <b>Seed-Inboxes</b> und <b>Placement-Heatmap</b>.
          </>,
          <>Bei Spam-Treffer bekommst du je nach Einstellung eine Telegram- oder Mail-Warnung.</>,
        ]}
        tips={[
          <>Alle 6 Stunden ist ein guter Start — öfter belastet die Test-Postfächer unnötig.</>,
          <>Mindestens 1 Gmail + 1 Outlook + 1 Web.de in den Seed-Inboxes haben.</>,
        ]}
        glossary={[
          {
            term: "Seed-Inbox",
            def: "Test-Postfach bei einem echten Anbieter (Gmail, Outlook, GMX…). Wir schicken Mails dahin, um zu sehen, ob sie im Posteingang oder Spam landen.",
          },
          {
            term: "Scheduler",
            def: "Zeitplaner. Sagt dem System: mach das alle X Stunden automatisch.",
          },
        ]}
      />

      <Card className="p-6 bg-card/60 border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold flex items-center gap-2">
              <Clock className="size-4" /> Scheduler-Konfiguration
            </h3>
            <p className="text-xs text-muted-foreground">
              Stelle eine externe IMAP-Scanner-URL bereit, da Cloudflare Workers kein TCP/IMAP
              unterstützen.
            </p>
          </div>
          {s && (
            <div className="text-right text-xs text-muted-foreground">
              <div>
                Zuletzt ausgelöst:{" "}
                {s.last_triggered_at ? new Date(s.last_triggered_at).toLocaleString() : "noch nie"}
              </div>
              {s.last_status && (
                <Badge
                  variant={s.last_status === "ok" ? "default" : "destructive"}
                  className="mt-1"
                >
                  {s.last_status}
                </Badge>
              )}
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2 flex items-center justify-between p-3 rounded-md border border-border bg-background/40">
            <div>
              <Label className="text-sm">Scheduler aktiv</Label>
              <p className="text-xs text-muted-foreground">
                Wenn deaktiviert, ignoriert der Hook alle Cron-Aufrufe.
              </p>
            </div>
            <Switch
              checked={form.enabled}
              onCheckedChange={(v) => setForm({ ...form, enabled: v })}
            />
          </div>

          <div>
            <Label className="text-xs">Intervall (Minuten)</Label>
            <Input
              type="number"
              min={1}
              max={1440}
              value={form.interval_minutes}
              onChange={(e) => setForm({ ...form, interval_minutes: Number(e.target.value) })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Steuert die pg_cron-Frequenz. Nach Änderung das Cron-Schedule neu setzen.
            </p>
          </div>
          <div>
            <Label className="text-xs">Lookback-Fenster (Minuten)</Label>
            <Input
              type="number"
              min={1}
              max={10080}
              value={form.lookback_minutes}
              onChange={(e) => setForm({ ...form, lookback_minutes: Number(e.target.value) })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Nur Mails ab jetzt minus diesem Fenster werden untersucht.
            </p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">IMAP-Scanner-URL</Label>
            <Input
              type="url"
              placeholder="https://imap-worker.example.com/scan"
              value={form.scanner_url}
              onChange={(e) => setForm({ ...form, scanner_url: e.target.value })}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Empfängt POST mit {`{ host, port, tls, username, password, since }`} und liefert{" "}
              {`{ results: [...] }`} zurück.
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Nicht nötig für Gmail-Inboxen, die per Google-OAuth angebunden sind — die werden
              direkt im Worker abgefragt. Für alle anderen Provider: kleiner Node-Service nötig.
              Quellcode + Deploy-Anleitung im Repo unter{" "}
              <code className="font-mono">external-scanner/</code>.
            </p>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Scanner-Bearer-Token (optional)</Label>
            <Input
              type="password"
              placeholder="••••••••"
              value={form.scanner_secret}
              onChange={(e) => setForm({ ...form, scanner_secret: e.target.value })}
            />
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending && <Loader2 className="size-4 mr-2 animate-spin" />}
            Speichern
          </Button>
          <Button
            variant="outline"
            onClick={() => triggerManual.mutate()}
            disabled={triggerManual.isPending}
          >
            {triggerManual.isPending ? (
              <Loader2 className="size-4 mr-2 animate-spin" />
            ) : (
              <Play className="size-4 mr-2" />
            )}
            Jetzt scannen
          </Button>
        </div>
      </Card>

      <Card className="p-6 bg-card/60 border-border">
        <h3 className="font-semibold mb-4">Letzte Läufe</h3>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Lade…</p>
        ) : runs.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch keine Läufe.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left py-2">Start</th>
                <th className="text-right py-2">Dauer</th>
                <th className="text-right py-2">Inboxes</th>
                <th className="text-right py-2">Ergebnisse</th>
                <th className="text-right py-2">Fehler</th>
                <th className="text-left py-2 pl-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} className="border-b border-border/40">
                  <td className="py-2">{new Date(r.started_at).toLocaleString()}</td>
                  <td className="py-2 text-right text-muted-foreground">
                    {r.duration_ms ?? "—"} ms
                  </td>
                  <td className="py-2 text-right">{r.inboxes_scanned}</td>
                  <td className="py-2 text-right text-success">{r.results_inserted}</td>
                  <td className="py-2 text-right text-destructive">{r.errors}</td>
                  <td className="py-2 pl-3">
                    {r.ok ? (
                      <span className="inline-flex items-center gap-1 text-success text-xs">
                        <CheckCircle2 className="size-3" /> OK
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-destructive text-xs"
                        title={r.error_message ?? ""}
                      >
                        <AlertTriangle className="size-3" /> {r.errors} Fehler
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
